import { Card, CardContent } from "@/components/ui/card";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import { cn } from "@/lib/utils";
import { LucideIcon } from "lucide-react";

interface KPICardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: LucideIcon;
  iconColor?: string;
  trend?: {
    value: number;
    label?: string;
  };
  accentColor?: string;
}

function KPICard({ title, value, subtitle, icon: Icon, iconColor, trend, accentColor = "border-l-primary" }: KPICardProps) {
  const getTrendIcon = () => {
    if (!trend) return null;
    if (trend.value > 0) return <TrendingUp className="h-3 w-3" />;
    if (trend.value < 0) return <TrendingDown className="h-3 w-3" />;
    return <Minus className="h-3 w-3" />;
  };

  const getTrendColor = () => {
    if (!trend) return "";
    if (trend.value > 0) return "text-green-600 bg-green-50";
    if (trend.value < 0) return "text-red-600 bg-red-50";
    return "text-muted-foreground bg-muted";
  };

  return (
    <Card className={cn("border-l-4 hover:shadow-md transition-shadow", accentColor)}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{title}</p>
            <div className="mt-2 flex items-baseline gap-2">
              <span className="text-2xl font-bold tracking-tight">{value}</span>
              {trend && (
                <span className={cn(
                  "inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs font-medium",
                  getTrendColor()
                )}>
                  {getTrendIcon()}
                  {Math.abs(trend.value)}%
                </span>
              )}
            </div>
            {subtitle && (
              <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>
            )}
          </div>
          <div className={cn(
            "p-3 rounded-xl bg-gradient-to-br from-muted/50 to-muted",
          )}>
            <Icon className={cn("h-6 w-6", iconColor || "text-primary")} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

interface ExecutiveSummaryProps {
  data: {
    vehicles?: { total: number; active: number; inactive: number; inMaintenance: number };
    drivers?: { total: number; active: number };
    maintenance?: { totalOrders: number; pending: number; completed: number; totalCost: number };
    fuel?: { totalLiters: number; totalCost: number; avgEfficiency: number };
    costs?: { grandTotal: number; totalFuel: number; totalMaintenance: number; totalExpenses: number };
    documents?: { total: number; valid: number; expiringSoon: number; expired: number };
    alerts?: { total: number; critical: number; warning: number; info: number };
    trips?: { total: number; totalDistance: number; avgDuration: number };
  };
  filters: {
    vehicles?: boolean;
    drivers?: boolean;
    maintenance?: boolean;
    fuel?: boolean;
    costs?: boolean;
    documents?: boolean;
    alerts?: boolean;
    trips?: boolean;
  };
}

export function ExecutiveSummary({ data, filters }: ExecutiveSummaryProps) {
  const formatCurrency = (value: number) => 
    `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

  const formatNumber = (value: number) => 
    value.toLocaleString('pt-BR');

  // Icons imported inline to avoid issues
  const { Car, Users, Wrench, Fuel, DollarSign, FileText, AlertTriangle, MapPin } = require("lucide-react");

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Resumo Executivo</h3>
        <p className="text-sm text-muted-foreground">Visão consolidada dos principais indicadores</p>
      </div>
      
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {filters.vehicles && data.vehicles && (
          <KPICard
            title="Frota"
            value={data.vehicles.total}
            subtitle={`${data.vehicles.active} ativos • ${data.vehicles.inMaintenance} em manutenção`}
            icon={Car}
            iconColor="text-blue-500"
            accentColor="border-l-blue-500"
          />
        )}
        
        {filters.drivers && data.drivers && (
          <KPICard
            title="Motoristas"
            value={data.drivers.total}
            subtitle={`${data.drivers.active} ativos`}
            icon={Users}
            iconColor="text-green-500"
            accentColor="border-l-green-500"
          />
        )}
        
        {filters.maintenance && data.maintenance && (
          <KPICard
            title="Manutenções"
            value={data.maintenance.totalOrders}
            subtitle={`${data.maintenance.pending} pendentes • ${formatCurrency(data.maintenance.totalCost)}`}
            icon={Wrench}
            iconColor="text-orange-500"
            accentColor="border-l-orange-500"
          />
        )}
        
        {filters.fuel && data.fuel && (
          <KPICard
            title="Combustível"
            value={`${formatNumber(data.fuel.totalLiters)} L`}
            subtitle={`Média: ${data.fuel.avgEfficiency.toFixed(1)} km/L`}
            icon={Fuel}
            iconColor="text-purple-500"
            accentColor="border-l-purple-500"
          />
        )}
        
        {filters.costs && data.costs && (
          <KPICard
            title="Custo Total"
            value={formatCurrency(data.costs.grandTotal)}
            subtitle={`Combustível + Manutenção + Despesas`}
            icon={DollarSign}
            iconColor="text-emerald-500"
            accentColor="border-l-emerald-500"
          />
        )}
        
        {filters.documents && data.documents && (
          <KPICard
            title="Documentos"
            value={data.documents.total}
            subtitle={`${data.documents.expiringSoon} a vencer • ${data.documents.expired} vencidos`}
            icon={FileText}
            iconColor="text-cyan-500"
            accentColor="border-l-cyan-500"
          />
        )}
        
        {filters.alerts && data.alerts && (
          <KPICard
            title="Alertas"
            value={data.alerts.total}
            subtitle={`${data.alerts.critical} críticos • ${data.alerts.warning} atenção`}
            icon={AlertTriangle}
            iconColor="text-red-500"
            accentColor="border-l-red-500"
          />
        )}
        
        {filters.trips && data.trips && (
          <KPICard
            title="Viagens"
            value={data.trips.total}
            subtitle={`${formatNumber(data.trips.totalDistance)} km percorridos`}
            icon={MapPin}
            iconColor="text-indigo-500"
            accentColor="border-l-indigo-500"
          />
        )}
      </div>
    </div>
  );
}
